import { useFeatureFlags } from '@audibene/ta.react-feature-flags'
import { AppointmentStatus, defaultQueryOptions } from 'constants/'
import { createContext, ReactNode, useContext } from 'react'
import { useQuery, UseQueryResult } from 'react-query'
import type { Customer, OpportunityLifecycle } from 'types'
import { findLatestOpportunities, logFactory, pick } from 'utils'
import { useDeliveryModel } from '../useDeliveryModel'
import { useCustomer } from '../useCustomer'
import { useAppointmentsService, useOpportunityService } from '../services'
import { opportunityToLifecycle } from './opportunityToLifecycle'

type ProviderProps = {
  children: ReactNode
  opportunity?: OpportunityLifecycle
}

type UseHookContext = Omit<UseQueryResult, 'data'> &
  Partial<{
    customer: Customer
    isError: boolean
    opportunity: OpportunityLifecycle
  }>

const hookName = 'useOpportunity'
const log = logFactory(hookName)
const context = createContext<Partial<UseHookContext>>({})
const { Provider } = context

const camelFieldsForConsumers = [
  'country',
  'deliveryModel',
  'fitterSalesforceId',
  'gid',
  'ownerName',
  'salesforceId',
] as const

const activeAppointmentStatuses = [AppointmentStatus.COMPLETED, AppointmentStatus.CONFIRMED, AppointmentStatus.PENDING]

const OpportunityLifecycleProvider = ({ children, opportunity: opportunityProp }: ProviderProps) => {
  const { data: customer, isError: hasCustomerErrored, isLoading: isCustomerLoading } = useCustomer()
  const customerGid = customer?.salesforceId
  const actualCustomer = customerGid ? customer : undefined
  const [isNeoEnabled] = useFeatureFlags(['NEO'])
  const { deliveryModel } = useDeliveryModel()
  const { getLatestOpportunities, getCurrentOpportunity } = useOpportunityService()
  const { getAppointments } = useAppointmentsService()

  const fetchOpportunity = async (): Promise<OpportunityLifecycle | undefined> => {
    const params = { customerSalesforceId: [customerGid as string] }

    log(`🥰 Fetching opportunity...`, params)

    const { data } = await getLatestOpportunities(params)
    const { data: currentData } = await getCurrentOpportunity(params)
    const [found] = findLatestOpportunities(data, currentData?.[0], isNeoEnabled)

    log(`🥰 Found opportunity`, found)

    if (!found) return undefined

    const appointments = await getAppointments({
      opportunityGids: [found.salesforceId],
      statuses: activeAppointmentStatuses,
    })

    const hasAppointment = (appointments?.data?.length ?? 0) > 0
    const lifecycle = opportunityToLifecycle(found, { hasAppointment })

    if (!lifecycle) return undefined

    return { ...lifecycle, ...pick(found, camelFieldsForConsumers) } as OpportunityLifecycle
  }

  const {
    data: opportunity,
    isError: hasOpportunityLifecycleErrored,
    isLoading: isOpportunityLifecycleLoading,
    ...rest
  } = useQuery([hookName, customerGid, deliveryModel], fetchOpportunity, {
    ...defaultQueryOptions,
    enabled: Boolean(customerGid),
  })

  const isError = hasCustomerErrored || hasOpportunityLifecycleErrored
  const isLoading = isCustomerLoading || isOpportunityLifecycleLoading

  const contextValue: UseHookContext = {
    ...rest,
    customer: actualCustomer,
    isError,
    isLoading,
    opportunity: opportunityProp || opportunity,
  }

  return <Provider value={contextValue}>{children}</Provider>
}

const useOpportunityLifecycle = () => useContext(context)

export { context as OpportunityLifecycleContext, OpportunityLifecycleProvider, useOpportunityLifecycle }
